import React, { useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import minMax from 'dayjs/plugin/minMax';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import isNil from 'lodash/isNil';
import round from 'lodash/round';
import * as Yup from 'yup';
import { ModalFactory, PageTitle } from 'components';
import { appConfig } from 'config/app';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { useSetFirstSiteIfSingleSiteUser } from 'hooks';
import { FetchDataQueryType } from 'providers/ListContextProvider';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee, setCurrentEmployeeRequest } from 'store/App';
import { getCalculatorRulesRequest, selectCalculatorRules } from 'store/CalculatorRules';
import { getEmployeesRequest, getJobTitlesRequest, selectEmployees } from 'store/Collections';
import { selectCurrentEmployeeManagedSites, selectCurrentEmployeeOrg, selectCurrentEmployeeSite } from 'store/Employers';
import { Payment, payPaymentSetRequest } from 'store/Payment';
import { getSiteBalanceRequest, selectSiteAccountBalance, selectSitePOSIntegration, selectSiteSettings } from 'store/Site';
import { IStore } from 'store/types';
import { selectCurrentUserFullName } from 'store/User';
import { AffiliationType, DisbursementMethod, HoldbackType, IEmployee, IEmployerSite, IHoldbackFixed, IHoldbackPercent, IPayee, PayoutMethodTypes, TipCalculatorMethod } from 'types';
import { calculateHoldbackValue, centsToDollarsDisplay, formatDateForApi, getCurrentBusinessDay } from 'utils';
import { AltAlertContent, NoCalculatorRulesModal } from './Calculator/components/modals/NoCalculatorRulesModal/NoCalculatorRulesModal';
import { MakePaymentContent } from './MakePaymentContent';
import { MakePaymentStep } from './MakePaymentTypes';

dayjs.extend(isSameOrBefore);
dayjs.extend(minMax);

interface ISubmitPaymentPayload {
  payees: IPayee[];
  description: string;
  authorizedAgreement: boolean;
  businessDate: string;
  paymentCategory?: string;
}

interface IParams {
  step?: MakePaymentStep;
  paymentId?: string;
}

const MakePayment = () => {
  const params = useParams<IParams>();
  const dispatch = useDispatch();
  const [step, setStep] = useState<MakePaymentStep>(params.step ? params.step : MakePaymentStep.SelectSite);
  const [paymentMethod, setPaymentMethod] = useState<PayoutMethodTypes>(PayoutMethodTypes.STANDARD);
  const [mSidebarExpanded, setMSidebarExpanded] = useState<boolean>(false);
  const handleStepChange = (step: MakePaymentStep) => {
    setMSidebarExpanded(false);
    setStep(step);
  };

  const currentUserName = useSelector(selectCurrentUserFullName());
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentOrganizationId = currentEmployee?.organizationId;
  useSetFirstSiteIfSingleSiteUser();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentSiteId = currentEmployee?.siteId || '';
  const currentSitePosIntegration = useSelector(selectSitePOSIntegration());
  const currentCalculatorRules = useSelector(selectCalculatorRules());
  const currentEmployeeSites = useSelector(selectCurrentEmployeeManagedSites) || [];
  const siteBalance = useSelector(selectSiteAccountBalance());
  const currentOrganization = useSelector(selectCurrentEmployeeOrg);

  const {
    perTipMax,
    strictTipPool = false,
    holdback: siteHoldbacks,
    createPayments: createPaymentsEnabled,
    rolePolicy: siteRolePolicy,
    endDayMidnightOffset,
    timezone,
    overdraftLimit = 0,
    paymentCategories,
  } = useSelector(selectSiteSettings()) || {};

  const [tipPoolLimit, setTipPoolLimit] = useState<number | undefined>(undefined);

  const siteUsesHoldbacks = (holdbackSettings?: IHoldbackFixed | IHoldbackPercent) => {
    if (!holdbackSettings) return false;
    if (holdbackSettings?.type === HoldbackType.FIXED) {
      if ((holdbackSettings as IHoldbackFixed).amount === 0) {
        return false;
      }
    }
    return true;
  };

  const payeeFieldSchema = {
    tipAmount: Yup.number()
      .typeError('Invalid gross')
      .required('Invalid gross')
      .test('allowedMaxAndMin', 'Invalid gross', (value, ctx) => {
        if ((perTipMax || appConfig.TIP.MAX) < value - ctx.parent.holdbackAmount) return false;
        if (appConfig.TIP.MIN > value - ctx.parent.holdbackAmount) return false;
        return true;
      }),
    holdbackAmount: Yup.number()
      .typeError('Invalid gross')
      .test('moreThanTipAmount', 'Tip amount needs to be greater than holdback', (value, ctx) => ctx.parent.tipAmount > value),
    job: Yup.object().shape({
      classification: Yup.string(),
      title: Yup.string(),
    }),
    note: Yup.string().max(100, 'Invalid note'),
  };

  const changeTipValidationSchema = Yup.object().shape({
    payees: Yup.array().of(Yup.object().shape(payeeFieldSchema)).min(1, 'Must select at least one payee'),
    description: Yup.string().max(100, 'Description cannot be more than 100 characters'),
    businessDate: Yup.string()
      .test('businessDate', 'Invalid business date', (businessDate: string) => {
        const siteDate = dayjs.max(dayjs(dayjs().tz(timezone).format('YYYY-MM-DD')), dayjs(dayjs().format('YYYY-MM-DD')));
        return dayjs(businessDate, 'MM-DD-YYYY', true).isSameOrBefore(siteDate) && dayjs(businessDate, 'MM-DD-YYYY', true).isValid();
      })
      .required(),
    ...(step === MakePaymentStep.PaymentReview && {
      authorizedAgreement: Yup.boolean().oneOf([true], 'You must accept the terms of agreement to proceed'),
      paymentCategory: Yup.string(),
    }),
  });

  const currentBusinessDay = timezone && (endDayMidnightOffset || endDayMidnightOffset === 0) ? getCurrentBusinessDay(timezone, endDayMidnightOffset)?.format('MM-DD-YYYY') : undefined;

  const formik = useFormik({
    initialValues: {
      payees: [],
      description: '',
      authorizedAgreement: false,
      businessDate: currentBusinessDay,
    } as ISubmitPaymentPayload,
    onSubmit: ({ payees: pys, description, businessDate, paymentCategory }: ISubmitPaymentPayload) => {
      dispatch(
        payPaymentSetRequest({
          ...(description && { description }),
          paymentDate: formatDateForApi(businessDate),
          paymentCategory,
          payouts: pys.map((py) => {
            const gross = round(py.tipAmount as number, 2);
            const holdback = py.holdbackAmount || 0;
            const payoutType = 'tip';
            return {
              currency: 'usd',
              employeeId: py.id,
              gross,
              holdback,
              job: py.job,
              net: gross - holdback,
              ...(py.note && { notes: py.note }),
              type: payoutType,
            };
          }),
          siteId: currentSiteId,
          workflow: paymentMethod,
        })
      );
    },
    validationSchema: changeTipValidationSchema,
    validateOnBlur: true,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnMount: false,
  });

  const { payees } = formik.values;

  const totalTips = (payees || []).reduce((acc, payee: IPayee) => (payee.tipAmount || 0) + acc, 0);

  const totalInstantTips = (payees || []).filter((p) => p.disbursementMethod === DisbursementMethod.INSTANT).reduce((acc, payee: IPayee) => (payee.tipAmount || 0) + acc, 0);

  const totalPayrollTips = (payees || []).filter((p) => p.disbursementMethod === DisbursementMethod.PAYROLL).reduce((acc, payee: IPayee) => (payee.tipAmount || 0) + acc, 0);

  const totalHoldbacks = (payees || []).reduce((acc, payee: IPayee) => (payee.holdbackAmount || 0) + acc, 0);

  const netInstant = totalInstantTips - totalHoldbacks;
  const totalBalancePlusOverdraft = (siteBalance || 0) + overdraftLimit;

  const isOverdraft = (): { showOverdraft: boolean; overdraftContinue: boolean; message?: string } => {
    const balance = isNil(siteBalance) ? 0 : (siteBalance as number);
    const newBalance = netInstant - balance;

    // do not show if net payout is equal or less than 0
    if (netInstant <= 0) return { showOverdraft: false, overdraftContinue: true };

    if (netInstant > 0 && netInstant > balance && netInstant <= totalBalancePlusOverdraft) {
      return {
        showOverdraft: true,
        overdraftContinue: true,
        message: `Your new balance will be ${newBalance <= 0 ? `${centsToDollarsDisplay(newBalance)}` : `-${centsToDollarsDisplay(newBalance)}`}. Your limit is -${centsToDollarsDisplay(overdraftLimit)}.`,
      };
    }

    if (netInstant > totalBalancePlusOverdraft) {
      return {
        showOverdraft: true,
        overdraftContinue: false,
        message: `Your new balance is below the limit of ${overdraftLimit > 0 ? `-${centsToDollarsDisplay(overdraftLimit)}` : centsToDollarsDisplay(overdraftLimit)}. Payment cannot be accepted.`,
      };
    }

    return { showOverdraft: false, overdraftContinue: true };
  };

  const apiIsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.CREATE_PAYMENT_REQUEST));
  const { data: employees } = useSelector(selectEmployees()) || { data: [], total: null };

  const clearPayeesList = () => {
    formik.resetForm();
  };

  const fetchEmployees = (query: FetchDataQueryType) => {
    if (currentOrganizationId) {
      const { q } = query;
      const nonStatusQuery = Object.values(q || []).filter((q) => q.key !== 'status');
      const options = {
        ...query,
        q: nonStatusQuery,
      };

      dispatch(getEmployeesRequest(AffiliationType.SITES, currentSiteId, options));
    }
  };

  useEffect(() => {
    if (currentSite?.id && currentSite?.settings?.tipCalculator) {
      dispatch(getCalculatorRulesRequest(currentSite.id));
    }
  }, [currentSite?.id]);

  const pageHeaderContent = currentSite && <PageTitle title={currentSite.name} subtitle={`Account Balance: ${siteBalance || siteBalance === 0 ? centsToDollarsDisplay(siteBalance) : '--'}`} />;

  useEffect(() => {
    if (currentSiteId) {
      dispatch(getSiteBalanceRequest(currentSiteId));
      dispatch(getJobTitlesRequest({ siteId: currentSiteId, includeInactive: false }));
    }
  }, [dispatch, currentSiteId]);

  const onSelectSite = (selectedSite: IEmployerSite) => {
    if (selectedSite?.id !== currentSiteId) clearPayeesList(); // Clear payee list if selected site has changed.
    dispatch(setCurrentEmployeeRequest({ ...currentEmployee, siteId: selectedSite?.id }));
  };

  const [showNoCalcRulesModal, hideNoCalcRulesModal] = useModal(() => <NoCalculatorRulesModal hideModal={hideNoCalcRulesModal} closeOnOutSideClick />, []);

  const [showIntegrationRequiredModal, hideIntegrationRequiredModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hideIntegrationRequiredModal} isActive>
        <AltAlertContent
          hideLeftAction
          title="Integration Required"
          message="To use this feature, you'll need to integrate your Point of Sale (POS) system. Please contact support to learn more about the integration process."
          rightActionLabel="Contact Support"
          onClose={hideIntegrationRequiredModal}
          onRightAction={() => window.open(appConfig.LINKS.SUPPORT)}
        />
      </ModalFactory>
    ),
    []
  );

  const onPayoutMethodSelect = (selectedMethod: PayoutMethodTypes) => {
    setPaymentMethod(selectedMethod);

    switch (selectedMethod) {
      case PayoutMethodTypes.STANDARD:
        setStep(MakePaymentStep.SelectPayees);
        browserHistory.push(routeConstants.private.makePaymentStandard);
        break;
      case PayoutMethodTypes.UPLOAD:
        clearPayeesList();
        setStep(MakePaymentStep.UploadPayment);
        browserHistory.push(routeConstants.private.makePaymentUpload);
        break;
      case PayoutMethodTypes.CALCULATOR:
        clearPayeesList();
        if (!currentCalculatorRules || currentCalculatorRules?.method === TipCalculatorMethod.NONE) {
          showNoCalcRulesModal();
        } else if (!currentSitePosIntegration) {
          showIntegrationRequiredModal();
        } else {
          browserHistory.push(routeConstants.private.makePaymentCalculatorDate);
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!params.step) {
      handleStepChange(MakePaymentStep.SelectSite);
      browserHistory.push(routeConstants.private.makePayment);
    } else if (params?.step && !siteRolePolicy) {
      browserHistory.push(routeConstants.private.makePayment);
    }
  }, [params.step]);

  // get overdraft warning and message
  const overdraftWarning = isOverdraft();

  return step && payees && currentOrganization && currentOrganizationId ? (
    <FormikProvider value={formik}>
      <FieldArray
        name="payees"
        render={(arrayHelpers) => {
          const onRemovePayee = (index: number) => {
            arrayHelpers.remove(index);
          };

          const onRemoveAllPayees = () => {
            formik.setFieldValue('payees', []);
            formik.setFieldTouched('payees', false);
          };

          const onAddToPayeesList = (id: string, options?: { isBulkUpload?: boolean; importedTipAmount?: number }) => {
            const selectedEmployee = (employees || []).find((e) => e.id === id);
            const selectedEmployeeSite = (selectedEmployee?.sites || []).find((s) => s.id === currentSiteId);

            const selectedEmployeeSiteJob = selectedEmployeeSite?.job || { title: DEFAULT_ORG_ADMIN_JOB_TITLE };
            if (!(payees || []).find((p) => p.id === id)) {
              const selectedPayee = {
                ...selectedEmployee,
                ...{ site: selectedEmployeeSite },
                ...{ disbursementMethod: selectedEmployeeSite?.disbursementMethod || DisbursementMethod.INSTANT },
                ...{ job: selectedEmployeeSiteJob },
                ...{ tipAmount: options?.importedTipAmount || undefined },
                ...{ holdbackAmount: calculateHoldbackValue(selectedEmployeeSite?.disbursementMethod || DisbursementMethod.INSTANT, siteHoldbacks, options?.importedTipAmount) },
              } as IPayee;
              arrayHelpers.push(selectedPayee);
            }
          };

          const onAddUploadPaymentToPayeesList = (employee: IEmployee, tipAmount: number) => {
            const { id } = employee;
            const selectedEmployee = employee;
            const selectedEmployeeSite = (selectedEmployee?.sites || []).find((s) => s.id === currentSiteId);
            const selectedEmployeeSiteJob = selectedEmployeeSite?.job || { title: DEFAULT_ORG_ADMIN_JOB_TITLE };
            if (!(payees || []).find((p) => p.id === id)) {
              const selectedPayee = {
                ...selectedEmployee,
                ...{ site: selectedEmployeeSite },
                ...{ job: selectedEmployeeSiteJob },
                ...{ tipAmount: tipAmount || undefined },
                ...{ disbursementMethod: selectedEmployeeSite?.disbursementMethod || DisbursementMethod.INSTANT },
                ...{
                  holdbackAmount: calculateHoldbackValue(selectedEmployeeSite?.disbursementMethod || DisbursementMethod.INSTANT, siteHoldbacks, tipAmount),
                },
              } as IPayee;
              arrayHelpers.push(selectedPayee);
            }
          };

          return (
            <MakePaymentContent
              isProcessing={apiIsLoading}
              fetchData={fetchEmployees}
              formik={formik}
              currentEmployee={currentEmployee}
              currentSite={currentSite}
              currentSiteId={currentSiteId}
              step={step}
              payees={payees}
              arrayHelpers={arrayHelpers}
              siteBalance={siteBalance || 0}
              setStep={handleStepChange}
              setMSidebarExpanded={setMSidebarExpanded}
              mSidebarExpanded={mSidebarExpanded}
              employees={employees}
              currentEmployeeSites={currentEmployeeSites}
              pageHeaderContent={pageHeaderContent}
              currentOrganization={currentOrganization}
              currentOrganizationId={currentOrganizationId}
              onAddToPayeesList={onAddToPayeesList}
              onAddUploadPaymentToPayeesList={onAddUploadPaymentToPayeesList}
              onRemovePayee={onRemovePayee}
              onRemoveAllPayees={onRemoveAllPayees}
              totalTips={totalTips}
              totalInstantTips={totalInstantTips}
              totalPayrollTips={totalPayrollTips}
              tipPoolLimit={tipPoolLimit}
              setTipPoolLimit={setTipPoolLimit}
              perTipMax={perTipMax}
              siteUsesHoldbacks={siteUsesHoldbacks(siteHoldbacks)}
              siteHoldbacks={siteHoldbacks}
              totalHoldbacks={totalHoldbacks}
              currentUserName={currentUserName}
              onSelectSite={onSelectSite}
              onPayoutMethodSelect={onPayoutMethodSelect}
              clearPayeesList={clearPayeesList}
              showOverdraftWarning={overdraftWarning.showOverdraft}
              overdraftText={overdraftWarning.message || ''}
              overdraftContinue={overdraftWarning.overdraftContinue}
              rolePolicy={siteRolePolicy}
              strictTipPool={strictTipPool}
              createPaymentsEnabled={createPaymentsEnabled}
              paymentCategories={paymentCategories}
            />
          );
        }}
      />
    </FormikProvider>
  ) : null;
};

export default MakePayment;
